/**
 * Durable-ish home for the checkout idempotency key.
 *
 * The key MUST survive a page reload: the original double-charge defect was a key
 * held in a per-mount useRef, so a vendor who reloaded after an ambiguous outcome
 * minted a fresh one and defeated every downstream guard.
 *
 * sessionStorage is best-effort, NOT guaranteed. This app renders inside a
 * third-party iframe on ptdiocese.org, so the store may be partitioned (Chrome) or
 * blocked (Safari ITP), and access can throw SecurityError outright. api.ts:2
 * records the same constraint for auth tokens, which is why this codebase uses no
 * web storage anywhere else.
 *
 * So: try to persist, never depend on it. When storage is denied the key lives in
 * the flow's module-memory slot for the life of the page and protection falls to
 * the server's open:<vendorId> lock and dup:<contentHash> guard -- which is exactly
 * why those two layers were kept rather than deleted.
 *
 * One key slot per checkout flow (Plan 2 Task 7, controller ruling B4). A key left
 * behind by an ambiguous checkout (PENDING/STUCK never rotates) must never be
 * picked up by a DIFFERENT checkout: the server finishes a captured attempt's
 * RECORDED cart regardless of the request body, so a borrowed key would complete
 * the old cart and answer 201 as if it were the new purchase. Each flow therefore
 * has its own storage key AND its own module-memory slot.
 */
export const CHECKOUT_FLOWS = ["public", "roster-addition", "replacement", "completion"] as const;
export type CheckoutFlow = (typeof CHECKOUT_FLOWS)[number];

/** The public checkout's name is UNCHANGED from v1, so a key stored by the
 *  deployed code is still found after deploy. Every other flow is suffixed. */
const PUBLIC_STORAGE_KEY = "vpd.checkout.idempotencyKey";

export function idempotencyStorageKey(flow: CheckoutFlow): string {
  return flow === "public" ? PUBLIC_STORAGE_KEY : `${PUBLIC_STORAGE_KEY}.${flow}`;
}

/** The client key must always die BEFORE the server's completed record (30 days)
 *  and well inside Vanco's undocumented key retention (F10). */
const KEY_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface StoredKey {
  key: string;
  mintedAt: number;
}

/** Per-flow module memory: the fallback when storage is denied. */
const memoryKeys = new Map<CheckoutFlow, StoredKey>();

function readStored(flow: CheckoutFlow): StoredKey | null {
  try {
    const raw = sessionStorage.getItem(idempotencyStorageKey(flow));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredKey>;
    if (typeof parsed.key !== "string" || typeof parsed.mintedAt !== "number") {
      return null; // malformed (or a pre-amendment bare string): treat as absent
    }
    return { key: parsed.key, mintedAt: parsed.mintedAt };
  } catch {
    return null; // denied storage OR unparseable value — same safe answer
  }
}

function writeStored(flow: CheckoutFlow, value: StoredKey): void {
  try {
    sessionStorage.setItem(idempotencyStorageKey(flow), JSON.stringify(value));
  } catch {
    // Storage denied. The flow's memory slot still holds it for this page.
  }
}

function mint(flow: CheckoutFlow): StoredKey {
  const fresh = { key: crypto.randomUUID(), mintedAt: Date.now() };
  memoryKeys.set(flow, fresh);
  writeStored(flow, fresh);
  return fresh;
}

function loadOrCreate(flow: CheckoutFlow): string {
  const existing = memoryKeys.get(flow) ?? readStored(flow);
  // age < 0 means a FUTURE mintedAt (clock rollback after minting): treat as
  // invalid rather than letting the key outlive its 24-hour contract.
  const age = existing ? Date.now() - existing.mintedAt : Infinity;
  if (existing && age >= 0 && age < KEY_MAX_AGE_MS) {
    memoryKeys.set(flow, existing);
    return existing.key;
  }
  return mint(flow).key;
}

function clear(flow: CheckoutFlow, options?: { memoryOnly?: boolean }): void {
  memoryKeys.delete(flow);
  if (options?.memoryOnly) return;
  try {
    sessionStorage.removeItem(idempotencyStorageKey(flow));
  } catch {
    // Nothing to do.
  }
}

/** One checkout flow's key slot. Pages reach it through usePaymentSubmission
 *  (lib/payment-flow.ts), which takes the flow name. */
export interface IdempotencyKeySlot {
  /** The persisted key for this flow, or a freshly minted one when it is absent,
   *  malformed, future-dated or 24 hours old. */
  loadOrCreate(): string;
  /** Server-commanded rotation: a confirmed decline (PAYMENT_FAILED), RETRY_NOW,
   *  or STALE_KEY — every one means the server proved no charge exists under the
   *  old key (or the old key belongs to a finished order) and wants a fresh one
   *  (F9/F25, and decisions/2026-07-22-decline-rotates-idempotency-key.md).
   *  Ambiguous outcomes NEVER rotate. */
  rotate(): string;
  /** Called once an order is confirmed, so the next checkout is a new payment. */
  clear(options?: { memoryOnly?: boolean }): void;
}

export function idempotencyKeySlot(flow: CheckoutFlow): IdempotencyKeySlot {
  return {
    loadOrCreate: () => loadOrCreate(flow),
    rotate: () => mint(flow).key,
    clear: (options) => clear(flow, options),
  };
}

// The unscoped functions below are the PUBLIC checkout's slot, kept so existing
// callers and tests (CheckoutPage.test.tsx is Task 7's pin) are unchanged. A new
// checkout uses idempotencyKeySlot(<its own flow>) — never these.

export function loadOrCreateIdempotencyKey(): string {
  return loadOrCreate("public");
}

/** The public flow's rotation; see IdempotencyKeySlot.rotate. */
export function rotateIdempotencyKey(): string {
  return mint("public").key;
}

/** Clears the public flow's key; see IdempotencyKeySlot.clear. */
export function clearIdempotencyKey(options?: { memoryOnly?: boolean }): void {
  clear("public", options);
}
